import { Component, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { PromotionHeader } from '../models/promotion-header.model';
import { PromotionType } from '../models/promotion-type.model';
import { CrudService } from '../services/crud.service';
import { environment } from 'src/environments/environment';
import { NotifyService } from '../services/notify.service';
import { DxBoxModule, DxButtonModule, DxDateBoxModule, DxLoadPanelModule, DxSelectBoxModule, DxSwitchModule, DxTextBoxModule } from 'devextreme-angular';

@Component({
    selector: 'app-clone-promotion',
    templateUrl: './clone-promotion.component.html',
    styleUrls: ['./clone-promotion.component.css'],
    imports: [ CommonModule, DatePipe, DxBoxModule, DxSelectBoxModule, DxTextBoxModule, DxDateBoxModule, DxSwitchModule, DxButtonModule, DxLoadPanelModule ],
    standalone: true
})
export class ClonePromotionComponent {
  @Output() clonePromotionValues: EventEmitter<any> = new EventEmitter<any>();
  currentPromotion: PromotionHeader = new PromotionHeader();
  promotionTypes: PromotionType[] = [];
  promotionType: number | undefined;
  buyingGroups: any[] = [];
  buyingGroupId: number | undefined;
  buyingStart: string | undefined;
  buyingEnd: string | undefined;
  description: string | undefined;
  rebate_clone: boolean = false;
  core_range_only: boolean = false;
  sourcePromotionId: number;
  sourceBuyingGroupId: number;
  sourcePromotionType: number;
  sourceDescription: string;
  loadingVisible: boolean = false;
  apiUrl: string = environment.baseApiUrl;

  constructor(private crudService: CrudService,
    private notifyService: NotifyService) {
      this.crudService.getData(`${this.apiUrl}buying-groups`).subscribe((data: any) => {
        this.buyingGroups = data
      });

      this.crudService.getData(`${this.apiUrl}promotion-types`).subscribe((data: any) => {
        this.promotionTypes = data
      });
  }

  ngAfterContentChecked() {
    if(localStorage.getItem('selected-promotion') !== null) {
      this.currentPromotion = JSON.parse(localStorage.getItem('selected-promotion') || '');
      if(this.currentPromotion.id !== this.sourcePromotionId) {
        // New source promotion selected — capture its identity for the self-clone
        // check and clear any overrides left from a previous clone, so the target
        // buying group is always an explicit choice (never inherited from the source).
        this.sourcePromotionId = this.currentPromotion.id;
        this.sourceBuyingGroupId = this.currentPromotion.buying_group_id;
        this.sourcePromotionType = this.currentPromotion.promotion_type;
        this.sourceDescription = this.currentPromotion.description;
        this.buyingGroupId = undefined;
        this.promotionType = undefined;
        this.description = undefined;
        this.buyingStart = undefined;
        this.buyingEnd = undefined;
        this.rebate_clone = false;
        this.core_range_only = false;
      }
      this.currentPromotion.description = this.currentPromotion.description + ' (COPY)';
    }
  }

  // The promotion period and dates are inherited from the source, so a clone with the
  // same buying group, type and description is an exact duplicate of the source and
  // will always fail the promotion_headers unique index.
  get isSelfClone(): boolean {
    const description = this.description !== undefined ? this.description : this.currentPromotion.description;
    const promotionType = this.promotionType !== undefined ? this.promotionType : this.sourcePromotionType;
    return this.buyingGroupId === this.sourceBuyingGroupId
        && promotionType === this.sourcePromotionType
        && description === this.sourceDescription;
  }

  get canClone(): boolean {
    return this.buyingGroupId !== undefined && !this.isSelfClone;
  }

  setPromotionType(e: any)
  {
    this.promotionType = e.value;
  }

  setBuyingGroup(e: any)
  {
    this.buyingGroupId = e.value;
  }

  setBuyingStart(e: any)
  {
    this.buyingStart = e.value;
  }

  setBuyingEnd(e: any)
  {
    this.buyingEnd = e.value;
  }

  setDescription(e: any)
  {
    this.description = e.value;
  }

  setRebateClone(e: any)
  {
    this.rebate_clone = e.value;
  }

  setCoreRangeOnly(e: any)
  {
    this.core_range_only = e.value;
  }

  cancel() {
    this.clonePromotionValues.emit(false);
  }

  clonePromotion()
  {
    if(this.buyingGroupId === undefined) {
      this.notifyService.warnUser('Select a target buying group before cloning.');
      return;
    }
    if(this.isSelfClone) {
      this.notifyService.warnUser('This clone would be an exact copy of the source promotion (same buying group, type and description). Change one of them first.');
      return;
    }
    this.loadingVisible = true;
    let path: string = `clone-promotion`;
    if(this.promotionType !== undefined)
      this.currentPromotion.promotion_type = this.promotionType;
    this.currentPromotion.buying_group_id = this.buyingGroupId;
    if(this.buyingStart !== undefined)
      this.currentPromotion.buying_start = this.buyingStart;
    if(this.buyingEnd !== undefined)
      this.currentPromotion.buying_end = this.buyingEnd;
    if(this.description !== undefined)
      this.currentPromotion.description = this.description;
    const queryParams: string[] = [];
    if(this.rebate_clone !== undefined && this.rebate_clone === true)
      queryParams.push('clone_rebate=1');
    if(this.core_range_only === true)
      queryParams.push('core_range_only=1');
    if(queryParams.length > 0)
      path = `clone-promotion?${queryParams.join('&')}`;

    delete this.currentPromotion.status;

    try {
      this.crudService.insertData(`${this.apiUrl}${path}`, this.currentPromotion).subscribe((data: any) => {
        console.log(data);
        this.loadingVisible = true;
        if(data.success) {
          this.notifyService.tellUser(data);
          this.showSkippedFeedback(data);
          this.loadingVisible = false;
          this.clonePromotionValues.emit(true);
        } else {
          this.notifyService.tellUser(data);
          this.loadingVisible = false;
          this.clonePromotionValues.emit(false);
        }
      });
    } catch (error) {
      console.log(error);
    }
  }

  // Skipped products come back on data.data.skipped (see PromotionHeaderController::clonePromotion) —
  // empty/absent whenever core_range_only wasn't ticked, so this is a no-op for every other clone.
  private showSkippedFeedback(data: any): void {
    const skipped = data?.data?.skipped;
    if(!Array.isArray(skipped) || skipped.length === 0) {
      return;
    }
    const buyingGroup = this.buyingGroups.find(bg => bg.id === this.buyingGroupId);
    const buyingGroupName = buyingGroup ? buyingGroup.buying_group_name : 'target buying group';
    const productList = skipped.map((product: any) => product.product_name).join(', ');
    this.notifyService.warnUser(`${skipped.length} product(s) skipped — not on the ${buyingGroupName} Core Range: ${productList}`);
  }
}
